import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import PublicNavbar from '../Layout/PublicNavbar';
import PasswordInput from '../Common/PasswordInput';
import SEO from '../Common/SEO';
import { analytics } from '../../utils/analytics';
import './Auth.css';

// Common disposable email domains for client-side validation
const COMMON_DISPOSABLE_DOMAINS = new Set([
  '10minutemail.com',
  '10minutemail.net',
  'dispostable.com',
  'fakeinbox.com',
  'getnada.com',
  'guerrillamail.biz',
  'guerrillamail.com',
  'guerrillamail.de',
  'guerrillamail.info',
  'guerrillamail.net',
  'guerrillamail.org',
  'mailinator.com',
  'mailinator.net',
  'mailinator.org',
  'maildrop.cc',
  'mohmal.com',
  'sharklasers.com',
  'spam4.me',
  'spamgourmet.com',
  'temp-mail.org',
  'temp-mail.ru',
  'tempmail.com',
  'tempmail.net',
  'throwawaymail.com',
  'trashmail.com',
  'trashmail.net',
  'yopmail.com',
  'yopmail.fr',
  'yopmail.net',
  'mailnesia.com',
  'mailcatch.com',
  'mintemail.com',
  'mytrashmail.com',
  'getairmail.com',
  'meltmail.com',
  'spambox.us',
  'tempemail.net',
  'throwam.com',
  'mailnull.com',
  'spamherelots.com',
  'anonymbox.com',
  'inboxalias.com',
  'mailsac.com',
  'tempinbox.com',
  'fakemailgenerator.com',
  'temporary-mail.net',
  'guerillamail.com',
]);

const isDisposableEmail = (email) => {
  if (!email || !email.includes('@')) return false;
  const domain = email.split('@')[1]?.toLowerCase();
  return domain && COMMON_DISPOSABLE_DOMAINS.has(domain);
};

function Register() {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [emailWarning, setEmailWarning] = useState('');
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  // Debounced check for disposable email
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (email && isDisposableEmail(email)) {
        setEmailWarning('This appears to be a temporary email. Please use a permanent email address.');
      } else {
        setEmailWarning('');
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [email]);

  // Password strength calculation
  const getPasswordStrength = (pwd) => {
    if (!pwd) return 0;
    let strength = 0;
    if (pwd.length >= 6) strength++;
    if (pwd.length >= 8) strength++;
    if (/[A-Z]/.test(pwd)) strength++;
    if (/[0-9]/.test(pwd)) strength++;
    if (/[^A-Za-z0-9]/.test(pwd)) strength++;
    return Math.min(strength, 4);
  };

  const getPasswordStrengthLabel = (pwd) => {
    const strength = getPasswordStrength(pwd);
    if (!pwd) return '';
    if (strength <= 1) return 'Weak';
    if (strength === 2) return 'Fair';
    if (strength === 3) return 'Good';
    return 'Strong';
  };

  const getPasswordStrengthColor = (pwd) => {
    const strength = getPasswordStrength(pwd);
    if (strength <= 1) return '#ef4444';
    if (strength === 2) return '#f59e0b';
    if (strength === 3) return '#10b981';
    return '#22c55e';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    const result = await register(firstName, lastName, email, password);
    
    if (result.success) {
      // Track signup event
      analytics.signUp();
      // Show success message about email verification
      const successMessage = result.message || 'Account created successfully. Please check your email to verify your account.';
      showToast(successMessage, 'success');
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <>
      <SEO
        title="Register"
        description="Create a free Options Tracker account. Track options trades, wheel strategy, covered calls, CSPs, and LEAPS. Free forever, no credit card required."
        canonicalPath="/register"
        keywords="options tracker signup, register options trading, free trading journal, options tracking signup"
        noindex={false}
      />
      <PublicNavbar />
      <div className="auth-container">
      <div className="auth-card">
        <h2>Register</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>First Name</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Last Name</label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {emailWarning && <div className="email-warning">{emailWarning}</div>}
          </div>
          <div className="form-group">
            <label>Password</label>
            <PasswordInput
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
            />
            {password && (
              <div className="password-strength" style={{ marginTop: '8px' }}>
                <div style={{
                  display: 'flex',
                  gap: '4px',
                  marginBottom: '4px'
                }}>
                  {[1, 2, 3, 4].map((level) => (
                    <div
                      key={level}
                      style={{
                        height: '4px',
                        flex: 1,
                        borderRadius: '2px',
                        background: level <= getPasswordStrength(password)
                          ? getPasswordStrengthColor(password)
                          : 'var(--border-color)'
                      }}
                    />
                  ))}
                </div>
                <small style={{
                  fontSize: '12px',
                  color: getPasswordStrengthColor(password)
                }}>
                  {getPasswordStrengthLabel(password)}
                </small>
              </div>
            )}
            <small className="form-text" style={{ display: 'block', marginTop: '5px', fontSize: '12px', color: 'var(--text-secondary)' }}>Must be at least 6 characters</small>
          </div>
          {error && <div className="error">{error}</div>}
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Registering...' : 'Register'}
          </button>
        </form>
        <p style={{ marginTop: '15px', textAlign: 'center' }}>
          Already have an account? <Link to="/login">Login here</Link>
        </p>
      </div>
    </div>
    </>
  );
}

export default Register;

